import { useState, useEffect, useCallback, useMemo } from 'react'
import { useGet, usePost, usePut, usePatch, useDelete } from '../utils/apiComponent/useApi'
import { api } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'
import { useTranslation } from '../i18n'

/**
 * Hook pour gérer les FAQs publiques
 */
export function useFAQ(options = {}) {
  const { language: contextLanguage } = useTranslation()
  const {
    page = 1,
    limit = 10,
    search = '',
    category = '',
    isPublished = true,
    language = null,
    sortBy = 'order',
    sortOrder = 'asc',
    immediate = true
  } = options

  const currentLanguage = language || contextLanguage || 'fr'
  const [faqs, setFaqs] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Construire l'URL avec les paramètres de requête
  const faqsUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (page) params.append('page', page.toString())
    if (limit) params.append('limit', limit.toString())
    if (search) params.append('search', search)
    if (category) params.append('category', category)
    if (isPublished !== null) params.append('isPublished', isPublished.toString())
    if (currentLanguage) params.append('language', currentLanguage)
    if (sortBy) params.append('sortBy', sortBy)
    if (sortOrder) params.append('sortOrder', sortOrder)
    
    const queryString = params.toString()
    return queryString ? `${endpoints.faqs}?${queryString}` : endpoints.faqs
  }, [page, limit, search, category, isPublished, currentLanguage, sortBy, sortOrder])

  const { data: faqsData, loading: faqsLoading, error: faqsError, execute: refetch } = useGet(
    faqsUrl,
    { immediate, isPublicRoute: true }
  )

  useEffect(() => {
    setLoading(faqsLoading)
    setError(faqsError)
  }, [faqsLoading, faqsError])

  useEffect(() => {
    if (faqsData) {
      // Gérer la structure de réponse API: { success: true, data: { faqs: [...], total, page, totalPages } }
      let data = faqsData
      if (faqsData.data) {
        data = faqsData.data
      }
      
      if (Array.isArray(data)) {
        setFaqs(data)
        setTotal(data.length)
        setTotalPages(1)
      } else if (data.faqs && Array.isArray(data.faqs)) {
        setFaqs(data.faqs)
        setTotal(data.total || 0)
        setTotalPages(data.totalPages || 1)
      } else if (Array.isArray(data)) {
        setFaqs(data)
        setTotal(data.length)
        setTotalPages(1)
      }
    }
  }, [faqsData])

  return {
    faqs,
    total,
    totalPages,
    loading,
    error,
    refetch
  }
}

/**
 * Hook pour récupérer les FAQs publiées uniquement
 */
export function usePublishedFAQs(options = {}) {
  const { language: contextLanguage } = useTranslation()
  const {
    category = '',
    language = null
  } = options

  const currentLanguage = language || contextLanguage || 'fr'
  const [faqs, setFaqs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const publishedUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    if (currentLanguage) params.append('language', currentLanguage)
    
    const queryString = params.toString()
    return queryString ? `${endpoints.faqsPublished}?${queryString}` : endpoints.faqsPublished
  }, [category, currentLanguage])

  const { data: faqsData, loading: faqsLoading, error: faqsError, execute: refetch } = useGet(
    publishedUrl,
    { isPublicRoute: true }
  )

  useEffect(() => {
    setLoading(faqsLoading)
    // Convertir l'erreur en string si c'est un objet
    if (faqsError) {
      const errorMessage = typeof faqsError === 'string' 
        ? faqsError 
        : faqsError?.message || faqsError?.toString() || 'Une erreur est survenue'
      setError(errorMessage)
    } else {
      setError(null)
    }
  }, [faqsLoading, faqsError])

  useEffect(() => {
    if (faqsData) {
      // Gérer la structure de réponse API: { success: true, data: [...] }
      let data = faqsData
      if (faqsData.data && Array.isArray(faqsData.data)) {
        data = faqsData.data
      } else if (faqsData.data && !Array.isArray(faqsData.data)) {
        // Si data n'est pas un tableau, essayer de trouver les FAQs ailleurs
        data = faqsData
      }
      
      if (Array.isArray(data)) {
        setFaqs(data)
      } else if (data.faqs && Array.isArray(data.faqs)) {
        setFaqs(data.faqs)
      } else {
        setFaqs([])
      }
    } else {
      // Réinitialiser si pas de données
      setFaqs([])
    }
  }, [faqsData])

  return {
    faqs,
    loading,
    error,
    refetch
  }
}

/**
 * Hook pour récupérer les catégories de FAQ
 * @param {Object} options - Options pour le hook
 * @param {string} options.language - Code langue (fr, en, es, etc.)
 */
export function useFAQCategories(options = {}) {
  const { language: contextLanguage } = useTranslation()
  const { language = null } = options
  const currentLanguage = language || contextLanguage || 'fr'
  
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const categoriesUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (currentLanguage) params.append('language', currentLanguage)
    const queryString = params.toString()
    return queryString ? `${endpoints.faqCategories}?${queryString}` : endpoints.faqCategories
  }, [currentLanguage])

  const { data: categoriesData, loading: categoriesLoading, error: categoriesError, execute: refetch } = useGet(
    categoriesUrl,
    { isPublicRoute: true }
  )

  useEffect(() => {
    setLoading(categoriesLoading)
    // Convertir l'erreur en string si c'est un objet
    if (categoriesError) {
      const errorMessage = typeof categoriesError === 'string' 
        ? categoriesError 
        : categoriesError?.message || categoriesError?.toString() || 'Une erreur est survenue'
      setError(errorMessage)
    } else {
      setError(null)
    }
  }, [categoriesLoading, categoriesError])

  useEffect(() => {
    if (categoriesData) {
      // Gérer la structure de réponse API: { success: true, data: [...] }
      let data = categoriesData
      if (categoriesData.data && Array.isArray(categoriesData.data)) {
        data = categoriesData.data
      }
      
      if (Array.isArray(data)) {
        setCategories(data)
      } else if (data.categories && Array.isArray(data.categories)) {
        setCategories(data.categories)
      }
    }
  }, [categoriesData])

  return {
    categories,
    loading,
    error,
    refetch
  }
}

/**
 * Hook pour récupérer une FAQ spécifique
 */
export function useFAQDetail(faqId, options = {}) {
  const { language: contextLanguage } = useTranslation()
  const { language = null, immediate = true } = options
  const currentLanguage = language || contextLanguage || 'fr'

  const [faq, setFaq] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const faqUrl = useMemo(() => {
    if (!faqId) return null
    const params = new URLSearchParams()
    if (currentLanguage) params.append('language', currentLanguage)
    const queryString = params.toString()
    return queryString ? `${endpoints.getFAQ(faqId)}?${queryString}` : endpoints.getFAQ(faqId)
  }, [faqId, currentLanguage])

  const { data: faqData, loading: faqLoading, error: faqError, execute: refetch } = useGet(
    faqUrl,
    { immediate: immediate && !!faqId, isPublicRoute: true }
  )

  useEffect(() => {
    setLoading(faqLoading)
    // Convertir l'erreur en string si c'est un objet
    if (faqError) {
      const errorMessage = typeof faqError === 'string' 
        ? faqError 
        : faqError?.message || faqError?.toString() || 'Une erreur est survenue'
      setError(errorMessage)
    } else {
      setError(null)
    }
  }, [faqLoading, faqError])

  useEffect(() => {
    if (faqData) {
      if (faqData.data) {
        setFaq(faqData.data)
      } else {
        setFaq(faqData)
      }
    }
  }, [faqData])

  return {
    faq,
    loading,
    error,
    refetch
  }
}

/**
 * Hook pour incrémenter les vues d'une FAQ
 */
export function useIncrementFAQView() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const incrementView = useCallback(async (faqId) => {
    if (!faqId) return

    setLoading(true)
    setError(null)

    try {
      await api.post(endpoints.incrementFAQView(faqId), {}, { isPublicRoute: true })
      return { success: true }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de l\'incrémentation des vues'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    incrementView,
    loading,
    error
  }
}

/**
 * Hook pour soumettre un feedback (utile/pas utile) sur une FAQ
 */
export function useFAQFeedback() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submitFeedback = useCallback(async (faqId, isHelpful) => {
    if (!faqId || typeof isHelpful !== 'boolean') return

    setLoading(true)
    setError(null)

    try {
      const response = await api.post(endpoints.submitFAQFeedback(faqId), { isHelpful }, { isPublicRoute: true })
      return { success: true, data: response?.data || response }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la soumission du feedback'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    submitFeedback,
    loading,
    error
  }
}

/**
 * Hook pour gérer les FAQs côté admin
 */
export function useFAQManagement() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { execute: createFAQApi, loading: createLoading } = usePost(endpoints.createFAQ, { immediate: false })
  const { execute: updateFAQApi, loading: updateLoading } = usePatch(endpoints.updateFAQ(''), { immediate: false })
  const { execute: deleteFAQApi, loading: deleteLoading } = useDelete(endpoints.deleteFAQ(''), { immediate: false })

  const createFAQ = useCallback(async (faqData) => {
    setLoading(true)
    setError(null)

    try {
      const response = await createFAQApi(faqData)
      const result = response?.data || response
      return { success: true, data: result }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la création de la FAQ'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [createFAQApi])

  const updateFAQ = useCallback(async (faqId, faqData) => {
    if (!faqId) return { success: false, error: 'ID FAQ requis' }

    setLoading(true)
    setError(null)

    try {
      const response = await api.patch(endpoints.updateFAQ(faqId), faqData)
      const result = response?.data || response
      return { success: true, data: result }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la mise à jour de la FAQ'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteFAQ = useCallback(async (faqId) => {
    if (!faqId) return { success: false, error: 'ID FAQ requis' }

    setLoading(true)
    setError(null)

    try {
      await api.delete(endpoints.deleteFAQ(faqId))
      return { success: true }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la suppression de la FAQ'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    setLoading(createLoading || updateLoading || deleteLoading)
  }, [createLoading, updateLoading, deleteLoading])

  return {
    createFAQ,
    updateFAQ,
    deleteFAQ,
    loading,
    error
  }
}

/**
 * Hook pour gérer les traductions de FAQ
 */
export function useFAQTranslations(faqId) {
  const [translations, setTranslations] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: translationsData, loading: translationsLoading, error: translationsError, execute: refetch } = useGet(
    faqId ? endpoints.getFAQTranslations(faqId) : null,
    { immediate: !!faqId }
  )

  useEffect(() => {
    setLoading(translationsLoading)
    setError(translationsError)
  }, [translationsLoading, translationsError])

  useEffect(() => {
    if (translationsData) {
      if (translationsData.data && Array.isArray(translationsData.data)) {
        setTranslations(translationsData.data)
      } else if (Array.isArray(translationsData)) {
        setTranslations(translationsData)
      }
    }
  }, [translationsData])

  const createTranslation = useCallback(async (translationData) => {
    if (!faqId) return { success: false, error: 'ID FAQ requis' }
    
    const { language, question, answer, category } = translationData
    
    if (!language || !question || !answer) {
      return { success: false, error: 'Language, question et answer sont requis' }
    }

    setLoading(true)
    setError(null)

    try {
      const body = {
        language,
        question,
        answer,
        ...(category && { category })
      }
      const response = await api.post(endpoints.createFAQTranslation(faqId), body)
      const result = response?.data || response
      await refetch()
      return { success: true, data: result }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la création de la traduction'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [faqId, refetch])

  const updateTranslation = useCallback(async (language, translationData) => {
    if (!faqId) return { success: false, error: 'ID FAQ requis' }
    if (!language) return { success: false, error: 'Language requis' }

    setLoading(true)
    setError(null)

    try {
      const response = await api.patch(endpoints.updateFAQTranslation(faqId, language), translationData)
      const result = response?.data || response
      await refetch()
      return { success: true, data: result }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la mise à jour de la traduction'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [faqId, refetch])

  const deleteTranslation = useCallback(async (language) => {
    if (!faqId) return { success: false, error: 'ID FAQ requis' }

    setLoading(true)
    setError(null)

    try {
      await api.delete(endpoints.deleteFAQTranslation(faqId, language))
      await refetch()
      return { success: true }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la suppression de la traduction'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [faqId, refetch])

  return {
    translations,
    loading,
    error,
    refetch,
    createTranslation,
    updateTranslation,
    deleteTranslation
  }
}

/**
 * Hook pour récupérer une traduction spécifique d'une FAQ
 * @param {string} faqId - ID de la FAQ
 * @param {string} language - Code langue (en, fr, es, etc.)
 * @param {Object} options - Options pour le hook
 * @param {boolean} options.immediate - Charger immédiatement (défaut: true)
 */
export function useFAQTranslation(faqId, language, options = {}) {
  const { immediate = true } = options
  const [translation, setTranslation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const translationUrl = useMemo(() => {
    if (!faqId || !language) return null
    return endpoints.getFAQTranslation(faqId, language)
  }, [faqId, language])

  const { data: translationData, loading: translationLoading, error: translationError, execute: refetch } = useGet(
    translationUrl,
    { immediate: immediate && !!faqId && !!language }
  )

  useEffect(() => {
    setLoading(translationLoading)
    // Convertir l'erreur en string si c'est un objet
    if (translationError) {
      const errorMessage = typeof translationError === 'string' 
        ? translationError 
        : translationError?.message || translationError?.toString() || 'Une erreur est survenue'
      setError(errorMessage)
    } else {
      setError(null)
    }
  }, [translationLoading, translationError])

  useEffect(() => {
    if (translationData) {
      if (translationData.data) {
        setTranslation(translationData.data)
      } else {
        setTranslation(translationData)
      }
    }
  }, [translationData])

  return {
    translation,
    loading,
    error,
    refetch
  }
}
